import React, { useEffect, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Input,
  InputGroup,
  Row,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import DeleteModal from "../../Components/Common/DeleteModal";
import SupplierService from "../../services/OrderingServices/SupplierService";
import toast from "react-hot-toast";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const Supplier = () => {
  const [purchaseList, setPurchaseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [filterText, setFilterText] = useState("");
  const navigate = useNavigate();

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "75px",
    },
    { name: "Name", selector: (row) => row.name, sortable: true },
    {
      name: "Contact Person",
      selector: (row) => row.contactPerson,
      sortable: true,
    },
    { name: "Email", selector: (row) => row.email, sortable: true },
    { name: "Phone No", selector: (row) => row.phone, sortable: true },
    { name: "Pan No", selector: (row) => row.panNumber, sortable: true },
    { name: "Address", selector: (row) => row.address, sortable: true },
    {
      name: "Action",
      selector: (row) => (
        <div>
          <Button
            color="btn btn-soft-warning"
            className="btn-sm ms-1"
            onClick={() => navigate(`/suppliers/update/${row.id}`)}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            className="btn btn-soft-danger btn-sm ms-1"
            color="danger"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </div>
      ),
      center: true,
    },
  ];

  const fetchPurchase = async () => {
    try {
      setLoading(true);
      const response = await SupplierService.getList(1, 100);
      // add dropdownOpen field for mobile dropdown
      const listWithDropdown = response.map((item) => ({
        ...item,
        dropdownOpen: false,
      }));
      setPurchaseList(listWithDropdown);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("🚀 ~ error:", error);
    }
  };

  useEffect(() => {
    fetchPurchase();
  }, []);

  const handleFilterChange = (e) => setFilterText(e.target.value);

  const filteredData = purchaseList.filter((pkg) => {
    const searchContext = `${pkg?.name || ""} ${pkg?.phone || ""} ${
      pkg?.email || ""
    }`.toLowerCase();
    return searchContext.includes(filterText.toLowerCase());
  });

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDeleteClick = (itemId) => {
    setSelectedItem(itemId);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedItem) {
      try {
        await SupplierService.delete(selectedItem);
        setPurchaseList((prevList) =>
          prevList.filter((pkg) => pkg.id !== selectedItem)
        );
        toast.success("Selected supplier deleted successfully");
      } catch (error) {
        toast.error("Failed to delete the record");
      } finally {
        toggleDeleteModal();
        setSelectedItem(null);
      }
    }
  };

  const toggleDropdown = (id) => {
    setPurchaseList((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, dropdownOpen: !item.dropdownOpen }
          : { ...item, dropdownOpen: false }
      )
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Suppliers List" pageTitle="Suppliers" />

        {/* Search & Create */}
        <Row className="mb-2">
          <Col md={8} xs={12} className="mb-2">
            <InputGroup>
              <Input
                placeholder="Search by name, email, or phone"
                value={filterText}
                onChange={handleFilterChange}
              />
            </InputGroup>
          </Col>
          <Col
            md={4}
            xs={12}
            className="d-flex justify-content-end align-items-end"
          >
            <Link to="/suppliers/create">
              <button className="btn btn-success px-2">
                + Create supplier
              </button>
            </Link>
          </Col>
        </Row>

        {/* Desktop Table */}
        <div className="d-none d-lg-block">
          <Card>
            <CardBody>
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                persistTableHead
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle
                      visible={true}
                      height="80"
                      width="80"
                      color="#5B71B9"
                    />
                    <h5 className="mt-1">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={filteredData}
              />
            </CardBody>
          </Card>
        </div>

        {/* Mobile Cards */}
        <div className="d-block d-lg-none">
          {loading ? (
            <div className="d-flex justify-content-center my-4">
              <Triangle visible={true} height="60" width="60" color="#5B71B9" />
            </div>
          ) : filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              // <Card key={item.id} className="mb-2 shadow-sm">
              //   <CardBody>
              <Row>
                <Col xs={12}>
                  <Card className="mb-3 shadow-sm border-1">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">Supplier #{index + 1}</h6>
                        <Dropdown
                          isOpen={item.dropdownOpen}
                          toggle={() => toggleDropdown(item.id)}
                          direction="down"
                        >
                          <DropdownToggle
                            color="primary"
                            outline
                            className="btn-sm shadow-sm rounded-circle"
                          >
                            <i className="ri-more-2-fill"></i>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              onClick={() =>
                                navigate(`/suppliers/update/${item.id}`)
                              }
                            >
                              <i className="ri-edit-line text-warning me-2"></i>{" "}
                              Edit
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ri-delete-bin-5-line text-danger me-2"></i>{" "}
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                      <Row className="mb-1">
                        <Col xs={5} className="text-muted fw-medium">
                          Name:
                        </Col>
                        <Col xs={7}>{item.name}</Col>
                      </Row>

                      <Row className="mb-1">
                        <Col xs={5} className="text-muted fw-medium">
                          Contact Person:
                        </Col>
                        <Col xs={7}>{item.contactPerson}</Col>
                      </Row>

                      <Row className="mb-1">
                        <Col xs={5} className="text-muted fw-medium">
                          Email:
                        </Col>
                        <Col xs={7}>{item.email}</Col>
                      </Row>

                      <Row className="mb-1">
                        <Col xs={5} className="text-muted fw-medium">
                          Phone:
                        </Col>
                        <Col xs={7}>{item.phone}</Col>
                      </Row>

                      <Row className="mb-1">
                        <Col xs={5} className="text-muted fw-medium">
                          Pan No:
                        </Col>
                        <Col xs={7}>{item.panNumber}</Col>
                      </Row>

                      <Row>
                        <Col xs={5} className="text-muted fw-medium">
                          Address:
                        </Col>
                        <Col xs={7}>{item.address}</Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              //   </CardBody>
              // </Card>
            ))
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-0">No supplier records found.</p>
            </div>
          )}
        </div>

        {/* Delete Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedItem}
        />
      </div>
    </div>
  );
};

export default Supplier;
